import { InfiniteData, QueryClient, QueryKey } from "@tanstack/react-query";

import assertField from "@/lib/utils/assert-field";
import { logOnDev } from "@/services/axios/interceptors/response";
import { PaginationResponse } from "@/types/table";

import {
  BaseQueryData,
  PusherUpdateDetailDataProp,
  PusherUpdateEventData,
} from "../../types";

function isSearchListQueryKey({
  entity,
  queryKey,
}: {
  entity: readonly unknown[];
  queryKey: QueryKey;
}): boolean {
  if (queryKey.length < 3) return false;

  const numberOfEntities = entity.length;

  if (numberOfEntities + 1 >= queryKey.length) return false;

  const mismatchFound = entity.some(
    (entityItem, index) => queryKey[index] !== entityItem
  );

  if (mismatchFound) return false;

  if (queryKey[numberOfEntities] !== "list") return false;

  const filters = queryKey[numberOfEntities + 1];

  if (typeof filters !== "object" || !filters) return false;

  return "search" in filters;
}

function isPaginationResponse<T>(data: unknown): data is PaginationResponse<T> {
  if (typeof data !== "object" || !data) return false;
  return Object.hasOwn(data, "results");
}

function isInfiniteQueryResponse<T>(data: unknown): data is InfiniteData<T> {
  if (typeof data !== "object" || !data) return false;
  return Object.hasOwn(data, "pages");
}

function replaceItemById({
  items,
  data,
}: {
  items: BaseQueryData[];
  data: PusherUpdateDetailDataProp;
}): BaseQueryData[] {
  const updatedResults: BaseQueryData[] = JSON.parse(JSON.stringify(items));

  for (let i = 0; i < updatedResults.length; i++) {
    if (updatedResults[i].id === data.id) {
      updatedResults[i] = data.payload;
      break;
    }
  }

  return updatedResults;
}

export const baseOrganisationChannelUpdateFn = async (
  queryClient: QueryClient,
  data: PusherUpdateEventData
) => {
  logOnDev("[baseOrganisationChannelUpdateFn]", { data });
  const listQueryKey = [...data.entity, "list", ...(data.filters ?? [])].filter(
    Boolean
  );

  /**
   * If no id is provided, only invalidate the list
   */
  if (!assertField(data, "id")) {
    logOnDev("[baseOrganisationChannelUpdateFn] [invalidating LIST]", {
      listQueryKey,
    });
    await queryClient.invalidateQueries({
      queryKey: listQueryKey,
      exact: false,
    });
    return;
  }

  const detailQueryKey = [
    ...data.entity,
    "detail",
    data.id.toString(),
    ...(data.filters ? data.filters : []),
  ];

  /**
   * If an id is provided but no payload is provided, only invalidate the
   * detail.
   */
  if (!assertField(data, "payload")) {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: detailQueryKey,
      }),
      queryClient.removeQueries({
        predicate: query =>
          isSearchListQueryKey({
            entity: data.entity,
            queryKey: query.queryKey,
          }),
      }),
    ]);
    return;
  }

  await queryClient.removeQueries({
    predicate: query =>
      isSearchListQueryKey({ entity: data.entity, queryKey: query.queryKey }),
  });

  /**
   * If the id and payload are provided, then an update to an entity detail
   * occured. Therefore, set the query data to the new payload in the detail
   * cache, and all list cache.
   */
  // Set the query data for a specific item
  queryClient.setQueryData(detailQueryKey, data.payload);

  // Set the query data for a specific item in a list
  queryClient.setQueriesData(
    { queryKey: listQueryKey, exact: false, stale: false },
    oldData => {
      if (!oldData) return undefined;
      if (isPaginationResponse<BaseQueryData>(oldData)) {
        return {
          ...oldData,
          results: replaceItemById({ items: oldData.results, data }),
        };
      }
      if (isInfiniteQueryResponse(oldData)) {
        const updatedPages: PaginationResponse<BaseQueryData>[] = JSON.parse(
          JSON.stringify(oldData.pages)
        );

        outerLoop: for (const element of updatedPages) {
          for (let j = 0; j < element.results.length; j++) {
            if (element.results[j].id === data.id) {
              element.results[j] = data.payload;
              break outerLoop;
            }
          }
        }

        return {
          ...oldData,
          pages: updatedPages,
        };
      }
      if (Array.isArray(oldData)) {
        return replaceItemById({ items: oldData, data });
      }
      return undefined;
    }
  );
};
